<script setup>
import { computed } from 'vue'
import DonutChart from './DonutChart.vue'
import { CRITERIA, NO_PIPELINE } from '../lib/criteria.js'
import { money, num, pct } from '../lib/format.js'

const props = defineProps({
  groups: { type: Array, required: true },
  totals: { type: Object, required: true },
})
const emit = defineEmits(['abrir'])

/**
 * Los contactos que nunca entraron a un pipeline se sacan del pastel: son la
 * mayoria del total y, metidos como una porcion mas, aplastan la comparacion
 * entre proyectos, que es justo lo que el grafico tiene que dejar ver. Van
 * aparte, como cifra destacada.
 */
const sinPipeline = computed(() => props.groups.find((g) => g.id === NO_PIPELINE) || null)

const porProyecto = computed(() =>
  props.groups
    .filter((g) => g.id !== NO_PIPELINE && g.unmanaged > 0)
    .map((g) => ({ id: g.id, name: g.name, value: g.unmanaged })),
)

const totalProyectos = computed(() => porProyecto.value.reduce((s, i) => s + i.value, 0))

const cuotaSinPipeline = computed(() => {
  const total = props.totals.unmanaged || 1
  return (sinPipeline.value?.unmanaged || 0) / total
})

const porMotivo = computed(() =>
  Object.values(CRITERIA)
    .map((c) => ({ id: c.id, name: c.label, value: props.totals.byReason[c.id] || 0 }))
    .filter((m) => m.value > 0),
)

const filas = computed(() =>
  [...props.groups]
    .filter((g) => g.totalItems > 0 || g.unmanaged > 0)
    .sort((a, b) => b.unmanaged - a.unmanaged),
)

const maxUnmanaged = computed(() => Math.max(1, ...filas.value.map((f) => f.unmanaged)))

/** Motivo dominante del pipeline, para leer la tabla de un vistazo. */
function motivoPrincipal(g) {
  let mejor = null
  for (const [id, n] of Object.entries(g.byReason)) {
    if (n > 0 && (!mejor || n > mejor.n)) mejor = { id, n }
  }
  return mejor ? CRITERIA[mejor.id].label : '-'
}

function tono(rate) {
  if (rate >= 0.34) return 'critico'
  if (rate >= 0.15) return 'alto'
  return ''
}

function seleccionar(sector) {
  if (!sector || sector.id === '__otros__') return
  emit('abrir', sector.id)
}
</script>

<template>
  <div class="resumen">
    <div class="graficos">
      <section class="card grafico">
        <header>
          <h2>Reparto por proyecto</h2>
          <p class="muted">Leads no gestionados dentro de un pipeline. Pulsa una porcion para abrirlo.</p>
        </header>
        <DonutChart
          :items="porProyecto" :total="totalProyectos" etiqueta-total="en pipelines"
          @seleccionar="seleccionar"
        />
        <button v-if="sinPipeline?.unmanaged" class="fuera" @click="emit('abrir', sinPipeline.id)">
          <strong>{{ num(sinPipeline.unmanaged) }}</strong>
          <span>contactos mas sin oportunidad en ningun pipeline
            ({{ pct(cuotaSinPipeline) }} del total). No entran en el grafico.</span>
        </button>
      </section>

      <section class="card grafico">
        <header>
          <h2>Reparto por motivo</h2>
          <p class="muted">Un lead puede acumular varios motivos.</p>
        </header>
        <DonutChart :items="porMotivo" etiqueta-total="motivos" />
      </section>
    </div>

    <section class="card tabla">
      <header class="row">
        <h2 class="grow">Todos los proyectos</h2>
        <span class="muted">{{ num(filas.length) }} pipelines · ordenados por leads sin gestionar</span>
      </header>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Proyecto</th>
              <th class="right">Sin gestionar</th>
              <th class="reparto">Reparto</th>
              <th class="right">Del pipeline</th>
              <th class="right">Valor en riesgo</th>
              <th class="right nowrap">Media sin avanzar</th>
              <th>Motivo principal</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="g in filas" :key="g.id" class="fila" @click="emit('abrir', g.id)">
              <td class="nombre">{{ g.name }}</td>
              <td class="right mono fuerte">{{ num(g.unmanaged) }}</td>
              <td class="reparto">
                <span class="barra">
                  <i :style="{ width: (g.unmanaged / maxUnmanaged * 100) + '%' }" />
                </span>
              </td>
              <td class="right mono" :class="tono(g.rate)">{{ pct(g.rate) }}</td>
              <td class="right mono">{{ g.value ? money(g.value) : '-' }}</td>
              <td class="right mono">{{ g.avgDaysStuck }} d</td>
              <td class="muted">{{ motivoPrincipal(g) }}</td>
            </tr>
            <tr v-if="!filas.length">
              <td colspan="7" class="empty">Sin datos en esta ventana.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<style scoped>
.resumen { display: grid; gap: 14px; }
.graficos { display: grid; grid-template-columns: repeat(auto-fit, minmax(420px, 1fr)); gap: 14px; }
.grafico { padding: 16px; }
.grafico header { margin-bottom: 14px; }
.grafico p { margin: 2px 0 0; font-size: 12.5px; }
.fuera {
  display: flex; gap: 10px; align-items: baseline; width: 100%; text-align: left;
  margin-top: 14px; padding: 10px 12px; border-radius: 8px;
  border: 1px solid color-mix(in srgb, var(--critico) 28%, transparent);
  background: color-mix(in srgb, var(--critico) 8%, transparent);
}
.fuera:hover { background: color-mix(in srgb, var(--critico) 14%, transparent); }
.fuera strong { font-size: 19px; color: var(--critico); }
.fuera span { font-size: 12.5px; color: var(--muted); }
.tabla { overflow: hidden; }
.tabla > header { padding: 14px 16px; border-bottom: 1px solid var(--border); }
.fila { cursor: pointer; }
.nombre { font-weight: 600; }
.fuerte { font-weight: 700; }
.critico { color: var(--critico); font-weight: 600; }
.alto { color: var(--alto); font-weight: 600; }
.reparto { width: 160px; }
.barra { display: block; height: 7px; background: var(--surface-2); border-radius: 999px; overflow: hidden; }
.barra i { display: block; height: 100%; background: var(--accent); border-radius: 999px; }
@media (max-width: 720px) { .reparto { display: none; } }
</style>
